import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Scores on an annexure that are not on its (effective) scale, grouped with counts. */
export function docScaleMismatch(project, d) {
  const levels = (d.scoring_scheme?.levels?.length ? d.scoring_scheme.levels : (project.scoring_scheme?.levels || [])).map(l => l.label);
  if (!levels.length) return { levels, mismatched: [], rows: 0 };
  const counts = {};
  (d.questions || []).forEach(q => { const s = String(q.client_score || '').trim(); if (s && !levels.includes(s)) counts[s] = (counts[s] || 0) + 1; });
  const mismatched = Object.entries(counts).sort().map(([score, count]) => ({ score, count }));
  return { levels, mismatched, rows: mismatched.reduce((n, m) => n + m.count, 0) };
}

const nearest = (score, levels) => {
  const n = parseFloat(score);
  const nums = levels.map(l => [l, parseFloat(l)]).filter(([, v]) => !Number.isNaN(v));
  if (Number.isNaN(n) || !nums.length) return '';
  return nums.reduce((best, cur) => (Math.abs(cur[1] - n) < Math.abs(best[1] - n) ? cur : best))[0];
};

/** One-click remap of off-scale scores onto the annexure's scale (suggests the nearest numeric level). */
export function ScaleRemapDialog({ project, doc, open, onOpenChange, onChanged }) {
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const info = doc ? docScaleMismatch(project, doc) : { levels: [], mismatched: [], rows: 0 };
  useEffect(() => {
    if (open && doc) {
      const m = {};
      info.mismatched.forEach(x => { m[x.score] = nearest(x.score, info.levels); });
      setMapping(m);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.id]);
  if (!doc) return null;
  const apply = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/documents/${doc.id}/scoring-scheme/remap`, { mapping }, { withCredentials: true });
      toast.success(`${r.data.remapped} score${r.data.remapped === 1 ? '' : 's'} remapped onto the annexure scale`);
      onChanged(); onOpenChange(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not remap'); }
    finally { setBusy(false); }
  };
  const rescore = async (scope) => {
    setRescoring(true);
    try {
      const r = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/documents/${doc.id}/scoring-scheme/rescore`, { scope }, { withCredentials: true });
      toast.success(`AI re-scored ${r.data.rescored} of ${r.data.targets} row${r.data.targets === 1 ? '' : 's'} against the scale definitions${r.data.unscored ? ` (${r.data.unscored} left for manual scoring)` : ''}`);
      onChanged(); onOpenChange(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not re-score'); }
    finally { setRescoring(false); }
  };
  const remappedCount = (doc.questions || []).filter(q => q.client_score_remapped && (q.response || '').trim()).length;
  const answeredOff = (doc.questions || []).filter(q => (q.response || '').trim() && String(q.client_score || '').trim() && !info.levels.includes(String(q.client_score).trim())).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="rfp-scale-remap-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" />{info.rows ? "Scores not on this annexure's scale" : 'Remapped scores'} — {doc.filename}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          {info.rows > 0 ? (
            <p className="text-slate-600">{info.rows} row{info.rows === 1 ? ' carries' : 's carry'} a client score that no longer exists on the scale <b>{info.levels.join(' / ')}</b>. Pick where each old score should go (nearest level suggested), clear it, or let AI read each answer and score it against the definitions.</p>
          ) : (
            <p className="text-slate-600">All scores on this annexure are on the scale <b>{info.levels.join(' / ')}</b>.</p>
          )}
          {info.rows > 0 && (
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="rfp-scale-remap-rows">
            {info.mismatched.map(m => (
              <div key={m.score} className="flex items-center gap-2 p-2" data-testid={`rfp-scale-remap-row-${m.score}`}>
                <span className="font-mono font-semibold text-[#0F2040] w-14">{m.score}</span>
                <span className="text-[10px] text-slate-500 w-14">{m.count} row{m.count === 1 ? '' : 's'}</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                <select value={mapping[m.score] ?? ''} onChange={e => setMapping(x => ({ ...x, [m.score]: e.target.value }))} className="h-7 text-xs border border-slate-200 rounded-md px-1.5 bg-white flex-1" data-testid={`rfp-scale-remap-select-${m.score}`}>
                  <option value="">Clear score (unscored)</option>
                  {info.levels.map(l => <option key={l} value={l}>Score {l}</option>)}
                </select>
              </div>
            ))}
          </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {info.rows > 0 && <Button size="sm" onClick={apply} disabled={busy || rescoring || !info.rows} className="h-7 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-scale-remap-apply">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Remap {info.rows} row{info.rows === 1 ? '' : 's'}
            </Button>}
            {answeredOff > 0 && (
              <Button size="sm" variant="outline" onClick={() => rescore('off_scale')} disabled={busy || rescoring} className="h-7 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                title="Instead of a nearest-number guess, AI reads each answer and picks the level whose definition fits" data-testid="rfp-scale-rescore-off">
                {rescoring ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}AI re-score {answeredOff} answered row{answeredOff === 1 ? '' : 's'}
              </Button>
            )}
            <span className="text-[10px] text-slate-400">The previous score is kept on each row for audit.</span>
          </div>
          {remappedCount > 0 && (
            <div className="flex items-center gap-2 border-t border-slate-100 pt-2 flex-wrap" data-testid="rfp-scale-remapped-note">
              <span className="text-[10px] text-slate-500">{remappedCount} row{remappedCount === 1 ? ' was' : 's were'} remapped by nearest number earlier.</span>
              <Button size="sm" variant="outline" onClick={() => rescore('remapped')} disabled={busy || rescoring} className="h-6 text-[10px] border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="rfp-scale-rescore-remapped">
                {rescoring ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}AI re-score them against the definitions
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
